/**
 * Recently-viewed properties store (#1138).
 *
 * Platform-distinct key (NOT ckit's `hrck_recently_viewed`): a JSON array of
 * validated Central property identifiers (the property `uuid` field,
 * PROP-shaped today — see `central-uuid.ts`), newest first, deduped, trimmed
 * to a bound. Producers record ONLY confirmed views: the property-details
 * hooks record from a successful property fetch (`common/property-details.ts`
 * and the booking form), never from an unvalidated slug or URL, so a bad
 * slug can't pollute the list. They record through `recordPagePropertyView`
 * (`page-property.ts`, #1151), which adds the page-identity gate: a widget
 * pinned to a property on a NON-property page renders the details but is
 * not a view. `recordPropertyView` below is the ungated store write.
 *
 * Tokens are compared as EXACT strings — Central ids are case-sensitive
 * ("PROP…" must never be case-folded), and anything failing validation is
 * silently ignored (never store a slug, a numeric WP post id, or garbage).
 *
 * Change signal: same dual pattern as favorites — a CustomEvent for same-tab
 * observers in OTHER widget bundles (IIFE-per-widget means no shared module
 * state), the native `storage` event for other tabs.
 */
import { isCentralPropertyUuid } from './central-uuid'

export const RECENTLY_VIEWED_STORAGE_KEY = 'homerunner_recently_viewed_v1'
export const RECENTLY_VIEWED_CHANGED_EVENT = 'hr-recently-viewed-changed'
export const RECENTLY_VIEWED_DEFAULT_MAX = 10

/** Pure prepend/dedupe/trim — unit-testable with no DOM. Exact-string dedupe. */
export function pushRecentlyViewed(
  list: readonly string[],
  centralPropertyUuid: string,
  max: number = RECENTLY_VIEWED_DEFAULT_MAX,
): string[] {
  const rest = list.filter((t) => t !== centralPropertyUuid)
  return [centralPropertyUuid, ...rest].slice(0, Math.max(1, max))
}

/** Stored list, defensively parsed: non-arrays and invalid entries drop out. */
export function getRecentlyViewed(): string[] {
  try {
    const raw = localStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (t): t is string => typeof t === 'string' && isCentralPropertyUuid(t),
    )
  } catch {
    // Unavailable/corrupt storage — recently-viewed is a nicety, never a failure.
    return []
  }
}

/**
 * Record a CONFIRMED property view. Silently ignores anything that is not a
 * valid Central property identifier.
 */
export function recordPropertyView(
  centralPropertyUuid: string,
  max: number = RECENTLY_VIEWED_DEFAULT_MAX,
) {
  if (!isCentralPropertyUuid(centralPropertyUuid)) return
  try {
    const next = pushRecentlyViewed(getRecentlyViewed(), centralPropertyUuid, max)
    localStorage.setItem(RECENTLY_VIEWED_STORAGE_KEY, JSON.stringify(next))
    window.dispatchEvent(new CustomEvent(RECENTLY_VIEWED_CHANGED_EVENT))
  } catch {
    // localStorage unavailable/full — degrade silently (guest nicety).
  }
}

/** Same-tab (CustomEvent) + cross-tab (`storage`) subscription helper. */
export function onRecentlyViewedChanged(cb: () => void): () => void {
  const onCustom = () => cb()
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === RECENTLY_VIEWED_STORAGE_KEY) cb()
  }
  window.addEventListener(RECENTLY_VIEWED_CHANGED_EVENT, onCustom)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(RECENTLY_VIEWED_CHANGED_EVENT, onCustom)
    window.removeEventListener('storage', onStorage)
  }
}
